import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'

// Admin: per-user drill-down with full participant management. Add/edit
// forms live on their own pages; deactivate/reactivate are inline actions
// (participants are never deleted).

export function AdminUserDetailPage() {
  const { t } = useTranslation()
  const { user: me, loading: authLoading } = useAuth()
  const { id } = useParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState(null)
  const [error, setError] = useState(null)

  function reload() {
    api.admin.users.get(id)
      .then(setData)
      .catch((e) => setError(e.code || 'server'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (authLoading || !me || me.role !== 'admin' || !id) return
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, me, id])

  async function toggle(p) {
    const inactive = !!p.deactivated_at
    if (!inactive && !confirm(t('participants.confirm_deactivate', { name: p.name }))) return
    setBusyId(p.id); setError(null)
    try {
      const call = inactive ? api.admin.participants.reactivate : api.admin.participants.deactivate
      await call(p.id)
      reload()
    } catch (e) {
      setError(e.code || 'server')
    } finally { setBusyId(null) }
  }

  if (authLoading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (!me || me.role !== 'admin') {
    return <main className="container"><p className="error">{t('admin.errors.not_admin')}</p></main>
  }
  if (loading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (error && !data) {
    return <main className="container">
      <p className="error">{t(`admin_users.errors.${error}`, { defaultValue: t(`auth.errors.${error}`, { defaultValue: error }) })}</p>
      <p><Link to="/admin/users">← {t('admin_users.back_to_list')}</Link></p>
    </main>
  }
  if (!data) return null

  const { user, participants } = data

  return (
    <main className="container">
      <p><Link to="/admin/users">← {t('admin_users.back_to_list')}</Link></p>

      <h1>{user.name}</h1>
      <p className="muted">{user.email} · {t(`admin_users.role_${user.role}`, { defaultValue: user.role })} · {user.language}</p>
      {user.phone && <p className="muted">{t('participants.phone')}: {user.phone}</p>}
      <p><Link to={`/admin/users/${user.id}/edit`} className="link-button">{t('admin_users.edit')}</Link></p>

      {error && <p className="error">{t(`participants.errors.${error}`, { defaultValue: error })}</p>}

      <section className="card">
        <div className="page-head">
          <h2>{t('admin_users.participants_title', { count: participants.length })}</h2>
          <Link to={`/admin/users/${user.id}/participants/new`}>
            <button type="button">+ {t('participants.add')}</button>
          </Link>
        </div>

        {participants.length === 0
          ? <p className="muted">{t('admin_users.no_participants')}</p>
          : <ul className="participants-list">
              {participants.map((p) => {
                const inactive = !!p.deactivated_at
                return (
                  <li key={p.id} className="participant-row">
                    <div className="participant-main" style={inactive ? { opacity: 0.55 } : undefined}>
                      <strong>{p.name}</strong>
                      {inactive ? <span className="badge muted-badge">{t('participants.inactive_badge')}</span> : null}
                      <div className="participant-meta">
                        {p.birthdate && <span>{p.birthdate}</span>}
                        {p.phone && <span>{p.phone}</span>}
                        {p.email && <span>{p.email}</span>}
                      </div>
                    </div>
                    <div className="participant-actions">
                      <Link to={`/admin/users/${user.id}/participants/${p.id}/edit`} className="link-button">
                        {t('common.edit')}
                      </Link>
                      <button
                        type="button"
                        className={`link-button${inactive ? '' : ' danger'}`}
                        disabled={busyId === p.id}
                        onClick={() => toggle(p)}
                      >
                        {inactive ? t('participants.reactivate') : t('participants.deactivate')}
                      </button>
                    </div>
                  </li>
                )
              })}
            </ul>}
      </section>
    </main>
  )
}
